import React from 'react'
import { LuChevronDown, LuChevronUp } from "react-icons/lu"
import { accessibleStatusColor, opaqueStatusTint, surfaceForMode } from "../../utils/statusColor"
import { useThemeContext } from "../../theme/ThemeContext"

// Convert #hex (#RGB or #RRGGBB) to rgba with alpha. Returns null if not a hex color.
const hexToRgba = (hex, alpha) => {
  if (typeof hex !== 'string' || hex[0] !== '#') return null
  let h = hex.slice(1)
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  if (h.length !== 6) return null
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const isNearWhite = (hex) => {
  const rgba = hexToRgba(hex, 1)
  if (!rgba) return false
  const [r, g, b] = rgba.match(/\d+/g).map(Number)
  return r > 235 && g > 235 && b > 235
}

const StatusBadge = ({
  label,
  bgColor = 'var(--color-error-500)',
  textColor = '#fff',
  width = 'min-w-[94px]',
  showArrow = false,
  isOpen = false,
  className = '',
  outlined = false,
}) => {
  // Status colours are company-configured and arrive from the API, so some of them
  // land below WCAG AA at badge size — "Pending" (#6172F3) measured 3.34:1 on stage.
  // Re-tinting the background cannot rescue that (3.5 dark / 3.33 light), so the
  // colour itself is slid along its own hue until it clears 4.5:1 on this surface.
  const { mode } = useThemeContext()
  const { dark, surface } = surfaceForMode(mode)
  const legible = accessibleStatusColor(textColor, { dark, surface })

  // Tint derived from the (solid) status color so the badge adapts to light AND dark
  // from ONE value. Composited to an opaque colour rather than left translucent: rows
  // can carry a semantic highlight that is lighter than the card, and a see-through
  // tint would let that shift the badge's contrast (measured 3.73:1 on an error row).
  const tint = !outlined && !isNearWhite(legible) ? opaqueStatusTint(legible, 0.16, surface) : null

  const style = outlined
    ? { background: 'transparent', color: legible, border: `1.5px solid ${legible}` }
    : { background: tint || bgColor, color: legible }

  // Dropdown variant (showArrow): text spans the width, chevron pinned to the end,
  // auto width + no wrap so longer labels ("Not Qualified") always fit on one line.
  const layout = showArrow
    ? 'justify-between gap-2 px-3 py-[5px] min-w-[120px] w-auto whitespace-nowrap'
    // Never wrap: a two-line label ("Status Change") used to stretch the whole table
    // row. The badge grows in width instead, keeping every row the same height.
    : `justify-center gap-1.5 px-2 py-[3px] whitespace-nowrap leading-[1.45] ${width}`

  return (
    <div
      style={style}
      className={`font-semibold label flex items-center rounded-md select-none ${layout} ${className}`}
    >
      <span className={showArrow ? 'flex-1 text-left' : ''}>{label}</span>
      {showArrow && (!isOpen ? <LuChevronDown size={16} className="shrink-0" /> : <LuChevronUp size={16} className="shrink-0" />)}
    </div>
  )
}

export default StatusBadge
